/**
 * The browser half of `printedLayout`: the page font, and the page's pixels.
 *
 * Arial, because that is what the PMS prints in. Liberation Sans is next
 * because it is metric-compatible with Arial and is what a tablet without
 * Arial has, so a value measures the same width on the desk and the tablet -
 * and the proportions below are read from whichever face the browser actually
 * resolved, so the baseline arithmetic holds even when neither is installed.
 */

import { ARIAL, type FontMetrics, type Raster } from '@/components/domain/printedLayout'

export const PAGE_FONT = 'Arial, "Liberation Sans", Helvetica, "Nimbus Sans", sans-serif'

const PROBE_PX = 100

let probe: CanvasRenderingContext2D | null | undefined

function context(): CanvasRenderingContext2D | null {
  if (probe !== undefined) return probe
  try {
    probe = document.createElement('canvas').getContext('2d')
  } catch {
    probe = null
  }
  if (probe) probe.font = `${PROBE_PX}px ${PAGE_FONT}`
  return probe
}

let metrics: FontMetrics | undefined

/** Cap height, ascent and descent of the page font, per unit of font size. */
export function pageFontMetrics(): FontMetrics {
  if (metrics) return metrics
  const measured = context()?.measureText('H')
  const read = {
    capHeight: (measured?.actualBoundingBoxAscent ?? NaN) / PROBE_PX,
    ascent: (measured?.fontBoundingBoxAscent ?? NaN) / PROBE_PX,
    descent: (measured?.fontBoundingBoxDescent ?? NaN) / PROBE_PX,
  }
  metrics = Object.values(read).every((v) => Number.isFinite(v) && v > 0) ? read : ARIAL
  return metrics
}

/**
 * Widths already measured. Every value on the page is measured on every
 * render, and the page re-renders whenever anything in the session moves;
 * on a tablet the canvas calls were a visible share of each render.
 */
const widths = new Map<string, number>()

/** How wide `text` is in the page font, per unit of font size. */
export function textWidthEm(text: string): number {
  const known = widths.get(text)
  if (known !== undefined) return known
  const ctx = context()
  const width = ctx ? ctx.measureText(text).width / PROBE_PX : text.length * 0.55
  if (widths.size > 2000) widths.clear()
  widths.set(text, width)
  return width
}

/**
 * The pixels of a loaded page image, or null when the browser will not hand
 * them over - no canvas, or an image from another origin. Page images come
 * from `/api/v1/files/...` on this origin, so that is a fallback, not a path.
 */
export function readRaster(image: HTMLImageElement): Raster | null {
  const { naturalWidth: width, naturalHeight: height } = image
  if (!width || !height) return null
  try {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return null
    ctx.drawImage(image, 0, 0)
    return { data: ctx.getImageData(0, 0, width, height).data, width, height }
  } catch {
    // A tainted canvas throws on read. The viewer papers over whole cells instead.
    return null
  }
}
